import * as XLSX from "xlsx";

// Types and Interfaces
export type ColumnNames = string[] | Record<string, string>;

export interface ExportOptions {
  fileName?: string;
  sheetName?: string;
  format?: "xlsx" | "csv";
}

export interface RowData {
  [key: string]: any;
}

/**
 * Exports JSON data to Excel (.xlsx) or CSV with optional column rename.
 *
 * @param jsonData - Array of objects to export (e.g. [{ a: 1, b: 2 }, { a: 3, b: 4 }])
 * @param columnNames - Column display names for the exported file.
 *   - Array: same order as keys from first row, e.g. ["Item Name", "Brand", "SKU"]
 *   - Object: map of dataKey -> displayName, e.g. { itemName: "Item Name", brandName: "Brand" }
 * @param options - Optional settings
 * @param options.fileName - File name without extension (default: "Export")
 * @param options.sheetName - Sheet name (Excel only) (default: "Sheet1")
 * @param options.format - Export format (default: "xlsx")
 */
export function exportToExcelOrCsv(
  jsonData: RowData[],
  columnNames: ColumnNames,
  options: ExportOptions = {}
): void {
  const {
    fileName = "Export",
    sheetName = "Sheet1",
    format = "xlsx",
  } = options;

  if (!Array.isArray(jsonData) || jsonData.length === 0) {
    console.warn("No data to export");
    return;
  }

  const keys = Object.keys(jsonData[0]);
  let headers: string[];

  if (Array.isArray(columnNames)) {
    headers = columnNames.length >= keys.length
      ? columnNames.slice(0, keys.length)
      : [...columnNames, ...keys.slice(columnNames.length)];
  } else if (columnNames && typeof columnNames === "object" && !Array.isArray(columnNames)) {
    headers = keys.map((k) => (columnNames[k] !== undefined ? columnNames[k] : k));
  } else {
    headers = keys;
  }

  const rows: string[][] = jsonData.map((row: RowData) =>
    keys.map((key) => (row[key] != null ? String(row[key]) : ""))
  );
  const sheetData: string[][] = [headers, ...rows];

  if (format === "csv") {
    const csv = sheetData
      .map((row) =>
        row.map((cell) => `"${String(cell).replace(/"/g, '""')}"`).join(",")
      )
      .join("\n");
    const blob = new Blob(["\uFEFF" + csv], { type: "text/csv;charset=utf-8;" });
    const url = URL.createObjectURL(blob);
    const link = document.createElement("a");
    link.href = url;
    link.download = `${fileName}.csv`;
    link.click();
    URL.revokeObjectURL(url);
    return;
  }

  const ws = XLSX.utils.aoa_to_sheet(sheetData);
  const wb = XLSX.utils.book_new();
  XLSX.utils.book_append_sheet(wb, ws, sheetName);
  XLSX.writeFile(wb, `${fileName}.xlsx`);
}

/**
 * Exports JSON to Excel with custom column names.
 * @param jsonData - Array of row objects
 * @param columnNames - Array of header names (same order as keys) or { key: "Display Name" }
 * @param fileName - Output file name without extension (default: "Export")
 * @param sheetName - Sheet name (default: "Sheet1")
 */
export function exportToExcel(
  jsonData: RowData[],
  columnNames: ColumnNames,
  fileName: string = "Export",
  sheetName: string = "Sheet1"
): void {
  exportToExcelOrCsv(jsonData, columnNames, {
    fileName,
    sheetName,
    format: "xlsx",
  });
}

/**
 * Exports JSON to CSV with custom column names.
 * @param jsonData - Array of row objects
 * @param columnNames - Array of header names (same order as keys) or { key: "Display Name" }
 * @param fileName - Output file name without extension (default: "Export")
 */
export function exportToCsv(
  jsonData: RowData[],
  columnNames: ColumnNames,
  fileName: string = "Export"
): void {
  exportToExcelOrCsv(jsonData, columnNames, {
    fileName,
    format: "csv",
  });
}